import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { FileSpreadsheet } from 'lucide-react'

import type { AnyDataGridContract } from '../lib/types'
import { useLoadMore } from '@/lib/use-load-more'
import { Button } from '@/ui/button'
import { DataTable, SortableTh, Th } from '@/ui/data-table'
import { FilterMultiSelect } from '@/ui/filter-multi-select'
import { LoadMoreSentinel } from '@/ui/load-more-sentinel'
import { LoadingNotice } from '@/ui/loading-notice'
import { ResizeHandle } from '@/ui/resize-handle'
import { RetryNotice } from '@/ui/retry-notice'
import { SearchToolbar } from '@/ui/search-toolbar'
import { VirtualTableRows } from '@/ui/virtual-table-rows'

import {
  DataGridContext,
  type DataGridProps,
  useGridContext,
} from './data-grid-context'
import {
  DataGridActiveFilters,
  DataGridFilter,
  DataGridFilterControl,
  DataGridFilters,
} from './data-grid-filters'
import { DataGridSavedViewTabs } from './data-grid-saved-view-tabs'
import { DataGridSavedViews as DataGridSavedViewsControl } from './data-grid-saved-views'
import { DataGridToolbarMenu } from './data-grid-toolbar-menu'

const MIN_COLUMN_WIDTH = 80
const MAX_COLUMN_WIDTH = 1_200

type ColumnSizing = {
  layoutKey: string
  widths: Record<string, number>
  manual: ReadonlySet<string>
}

const AUTO_COLUMN_SIZING: ColumnSizing = {
  layoutKey: '',
  widths: {},
  manual: new Set(),
}

function DataGridRoot<Contract extends AnyDataGridContract>(props: DataGridProps<Contract>) {
  return (
    <DataGridContext value={props as unknown as DataGridProps<AnyDataGridContract>}>
      <div data-slot="data-grid" className="flex min-h-0 flex-1 flex-col">
        {props.children ?? (
          <>
            {props.savedViewTabs ? <DataGridSavedViewTabs /> : null}
            <DataGridToolbar />
            <DataGridTable />
          </>
        )}
      </div>
    </DataGridContext>
  )
}

function DataGridToolbar() {
  const { grid, assistantFilter, searchLabel = grid.contract.id, toolbarActions } = useGridContext()
  return (
    <SearchToolbar
      value={grid.searchInput}
      onChange={grid.setSearch}
      label={`Search ${searchLabel}`}
      placeholder={`Search ${searchLabel}…`}
      assistantFilter={assistantFilter ?? grid.contract.id}
      maxLength={200}
    >
      {grid.searchInput.trim().length > 0
        && grid.searchInput.trim().length < grid.contract.searchMinimumLength ? (
          <span className="text-muted-foreground text-[11px]">
            Type at least {grid.contract.searchMinimumLength} characters to search.
          </span>
        ) : null}
      {toolbarActions}
      <DataGridActiveFilters />
      <DataGridToolbarMenu />
    </SearchToolbar>
  )
}

function DataGridSearch() {
  const { grid, assistantFilter, searchLabel = grid.contract.id } = useGridContext()
  return (
    <SearchToolbar
      value={grid.searchInput}
      onChange={grid.setSearch}
      label={`Search ${searchLabel}`}
      placeholder={`Search ${searchLabel}…`}
      assistantFilter={assistantFilter ?? grid.contract.id}
      maxLength={200}
    />
  )
}

function DataGridLoadMore() {
  const { grid, noun = 'items' } = useGridContext()
  if (!grid.canLoadMore) return null
  return (
    <Button
      type="button"
      variant="outline"
      disabled={grid.query.isFetchingNextPage}
      onClick={() => void grid.query.fetchNextPage()}
    >
      {grid.query.isFetchingNextPage ? 'Loading…' : `Load more ${noun}`}
    </Button>
  )
}

function DataGridSavedViews() {
  const { grid } = useGridContext()
  return <DataGridSavedViewsControl savedViews={grid.savedViews} />
}

function DataGridColumnChooser() {
  const { grid } = useGridContext()
  return (
    <FilterMultiSelect
      label="Columns"
      hideLabel
      values={grid.selectedColumns}
      allLabel="Columns"
      items={grid.schema.columns.map((column) => ({
        value: column.id,
        label: column.label,
      }))}
      onChange={grid.setSelectedColumns}
    />
  )
}

function DataGridExport() {
  const { grid } = useGridContext()
  if (!grid.canExport) return null
  return (
    <span className="inline-flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={grid.isExporting}
        onClick={() => void grid.exportCsv()}
      >
        <FileSpreadsheet aria-hidden className="size-3.5" />
        {grid.isExporting ? 'Exporting…' : 'Export CSV'}
      </Button>
      {grid.isExporting ? (
        <Button type="button" size="sm" variant="ghost" onClick={grid.cancelExport}>
          Cancel
        </Button>
      ) : null}
      {grid.exportError ? (
        <span role="alert" className="text-destructive text-xs">
          {grid.exportError.message}
        </span>
      ) : null}
    </span>
  )
}

function DataGridTable() {
  const {
    grid,
    caption,
    regionLabel,
    noun = 'items',
    singularNoun,
    renderers,
    emptyMessage,
    onRowOpen,
    additionalColumns = [],
    columnChooserColumns,
  } = useGridContext()
  const columns = grid.orderedColumns.map((id) => grid.schema.columns
    .find((column) => column.id === id)!).filter((column) =>
    column && (!columnChooserColumns || columnChooserColumns.includes(column.id)))
  const visibleColumnIds = [
    ...columns.map((column) => column.id),
    ...additionalColumns.map((column) => column.id),
  ]
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const resizeSession = useRef<{ column: string; widths: Record<string, number> } | null>(null)
  const [storedSizing, setColumnSizing] = useState<ColumnSizing>(AUTO_COLUMN_SIZING)
  const [containerWidth, setContainerWidth] = useState(0)
  const columnSignature = JSON.stringify(visibleColumnIds)
  const layoutKey = JSON.stringify([grid.sourceKey, columnSignature, containerWidth])
  const columnSizing = storedSizing.layoutKey === layoutKey ? storedSizing : {
    ...storedSizing,
    widths: Object.fromEntries(Object.entries(storedSizing.widths)
      .filter(([column]) => storedSizing.manual.has(column))),
  }
  const hasItems = grid.items.length > 0

  useLayoutEffect(() => {
    if (!scrollElement) return
    const updateWidth = () => setContainerWidth(scrollElement.clientWidth)
    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(scrollElement)
    return () => observer.disconnect()
  }, [scrollElement])

  useLayoutEffect(() => {
    if (!hasItems || containerWidth <= 0 || storedSizing.layoutKey === layoutKey) return
    const table = tableRef.current
    if (!table) return
    function captureWidths() {
      if (!table?.querySelector('tbody tr[data-index]')) return
      const widths = measureColumnWidths(table)
      if (Object.values(widths).some((width) => width <= 0) || Object.keys(widths).length === 0) return
      setColumnSizing((current) => current.layoutKey === layoutKey
        ? current
        : {
          ...current,
          layoutKey,
          widths: {
            ...Object.fromEntries(Object.entries(current.widths)
              .filter(([column]) => current.manual.has(column))),
            ...widths,
          },
        })
    }
    const observer = new ResizeObserver(captureWidths)
    observer.observe(table)
    const frame = requestAnimationFrame(captureWidths)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [hasItems, containerWidth, layoutKey, storedSizing.layoutKey])
  const setResizeSession = (column: string, dragging: boolean) => {
    resizeSession.current = dragging
      ? { column, widths: measureColumnWidths(tableRef.current) }
      : null
  }
  const resizeColumn = (column: string, width: number) => {
    const measured = measureColumnWidths(tableRef.current)
    const startingWidths = resizeSession.current?.column === column
      ? resizeSession.current.widths
      : measured
    setColumnSizing((current) => {
      return {
        layoutKey,
        widths: resizeColumnAndTrailing(
          startingWidths,
          visibleColumnIds,
          column,
          width,
        ),
        manual: new Set(current.manual).add(column),
      }
    })
  }
  const autoSizeColumn = (column: string) => setColumnSizing((current) => {
    if (!current.manual.has(column)) return current
    const manual = new Set(current.manual)
    manual.delete(column)
    return {
      ...current,
      layoutKey: '',
      widths: Object.fromEntries(Object.entries(current.widths)
        .filter(([id]) => manual.has(id))),
      manual,
    }
  })
  const pager = useLoadMore({
    ...grid.query,
    data: {
      pages: [{ items: grid.items, pageInfo: { hasNextPage: grid.canLoadMore } }],
    },
    hasNextPage: grid.canLoadMore,
  }, noun, `Load more ${noun}`, {
    criteriaKey: JSON.stringify([grid.sourceKey, grid.activeCriteria]),
    singularNoun,
  })
  const headers = columns.map((column) => {
    const sort = grid.activeCriteria.sort?.find((entry) => entry.column === column.id)
    const filterActive = grid.activeCriteria.filter?.some((filter) =>
      filter.column === column.id) ?? false
    const custom = renderers?.[column.id]?.header
    return (
      <DataGridColumnHeader
        key={column.id}
        columnId={column.id}
        last={column.id === visibleColumnIds.at(-1)}
        label={column.label}
        width={columnSizing.widths[column.id]}
        auto={!columnSizing.manual.has(column.id)}
        onWidthChange={(width) => resizeColumn(column.id, width)}
        onDragChange={(dragging) => setResizeSession(column.id, dragging)}
        onAutoSize={() => autoSizeColumn(column.id)}
        sortable={column.sortable}
        direction={sort?.direction}
        onSort={() => grid.toggleSort(column.id)}
        filterActive={filterActive}
        filterContent={column.filterable ? (open) => (
          <DataGridFilterControl column={column} presentation="content" open={open} />
        ) : undefined}
        onFilterOpenChange={(open) => {
          if (open && column.kind === 'category' && column.operators.includes('in')) {
            void grid.loadFacetOptions(column.id)
          }
        }}
      >
        {custom?.({
          column,
          sort,
          toggleSort: () => grid.toggleSort(column.id),
          grid,
        })}
      </DataGridColumnHeader>
    )
  })
  headers.push(...additionalColumns.map((column) => (
    <DataGridColumnHeader
      key={column.id}
      columnId={column.id}
      last={column.id === visibleColumnIds.at(-1)}
      label={column.label}
      width={columnSizing.widths[column.id]}
      auto={!columnSizing.manual.has(column.id)}
      onWidthChange={(width) => resizeColumn(column.id, width)}
      onDragChange={(dragging) => setResizeSession(column.id, dragging)}
      onAutoSize={() => autoSizeColumn(column.id)}
    />
  )))

  if (!grid.hasDisplayedData && (grid.query.isLoading || grid.awaitingDefaultView)) {
    return <LoadingNotice message={`Loading ${noun}…`} />
  }
  if (!grid.hasDisplayedData && (grid.query.isLoadingError
    || (grid.query.isRefetchError && !grid.query.isFetchNextPageError))) return (
    <RetryNotice
      className="p-6"
      message={grid.query.error.message}
      onRetry={() => void grid.query.refetch()}
    />
  )
  return (
    <div
      ref={setScrollElement}
      data-slot="data-grid-table"
      role="region"
      aria-label={regionLabel ?? caption}
      aria-busy={grid.isUpdating}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Keyboard users need to focus and scroll the virtualized region.
      tabIndex={0}
      className="min-h-64 flex-1 scroll-pt-8 overflow-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring/30 [&_[data-slot=data-table]]:overflow-visible [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:bg-background"
    >
      {grid.isUpdating ? <DataGridUpdatingCue key={grid.sourceKey} noun={noun} /> : null}
      {grid.hasDisplayedData && grid.query.isError && !grid.query.isFetchNextPageError ? (
        <div className="flex items-center gap-2 px-3 py-2" role="alert">
          <span className="text-destructive text-xs">
            {grid.query.error?.message ?? `Couldn’t update ${noun}.`}
          </span>
          <Button type="button" size="sm" variant="outline" onClick={() => void grid.query.refetch()}>
            Retry
          </Button>
        </div>
      ) : null}
      <DataTable
        tableRef={tableRef}
        tableStyle={sizedTableStyle(columnSizing.widths, visibleColumnIds)}
        fixedLayout={visibleColumnIds.length > 0
          && visibleColumnIds.every((column) => columnSizing.widths[column] !== undefined)}
        caption={caption}
        headers={headers}
        empty={pager.items.length === 0}
        emptyMessage={emptyMessage ?? `No ${noun} match.`}
        resultStatus={{
          count: grid.isPreviousData ? null : pager.items.length,
          hasMore: pager.hasNextPage,
          singular: singularNoun ?? noun.replace(/s$/, ''),
          plural: noun,
          changeKey: JSON.stringify([grid.sourceKey, grid.activeCriteria]),
        }}
        rowCount={pager.hasNextPage ? -1 : pager.items.length + 1}
      >
        <VirtualTableRows
          items={pager.items}
          getKey={(row) => row.key}
          estimateSize={46}
          scrollElement={scrollElement}
          columnCount={columns.length + additionalColumns.length}
          renderRow={(row, rowProps) => {
            const typedRow = Object.assign(row, {
              getValue: (column: string) => row.values[column],
            })
            return (
              <tr
                {...rowProps}
                tabIndex={onRowOpen ? 0 : undefined}
                aria-label={onRowOpen
                  ? `View ${String(row.values.name ?? row.key)}`
                  : undefined}
                onClick={() => onRowOpen?.(row)}
                onKeyDown={(event) => {
                  if (onRowOpen && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault()
                    onRowOpen(row)
                  }
                }}
                className="border-border hover:bg-muted border-b outline-none"
              >
                {columns.map((column) => {
                  const renderer = renderers?.[column.id]?.cell
                  const value = row.values[column.id]
                  return (
                    <td
                      key={column.id}
                      className="text-muted-foreground overflow-hidden px-3 py-2"
                      style={fixedColumnStyle(columnSizing.widths[column.id])}
                    >
                      {renderer
                        ? renderer({ row: typedRow, column, value, grid })
                        : formatValue(value, column.labels)}
                    </td>
                  )
                })}
                {additionalColumns.map((column) => (
                  <td
                    key={column.id}
                    className="overflow-hidden px-3 py-2"
                    style={fixedColumnStyle(columnSizing.widths[column.id])}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            )
          }}
        />
      </DataTable>
      <LoadMoreSentinel
        pager={pager}
        root={scrollElement}
        paused={grid.searchDebouncing || grid.isPreviousData || grid.isUpdating}
        className="m-3"
      />
    </div>
  )
}

function DataGridUpdatingCue({ noun }: { noun: string }) {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const timeout = setTimeout(() => setVisible(true), 300)
    return () => clearTimeout(timeout)
  }, [])
  if (!visible) return null
  return (
    <div aria-hidden="true" className="pointer-events-none sticky top-8 left-0 z-20 h-0 w-full">
      <span className="bg-background text-muted-foreground absolute top-2 right-3 rounded-md border px-2 py-1 text-xs shadow-sm">
        Updating {noun}…
      </span>
    </div>
  )
}
function DataGridColumnHeader({
  columnId,
  last,
  label,
  width,
  auto,
  onWidthChange,
  onDragChange,
  onAutoSize,
  sortable = false,
  direction,
  onSort,
  filterContent,
  filterActive,
  onFilterOpenChange,
  children,
}: {
  columnId: string
  last: boolean
  label: string
  width?: number
  auto: boolean
  onWidthChange: (width: number) => void
  onDragChange: (dragging: boolean) => void
  onAutoSize: () => void
  sortable?: boolean
  direction?: 'asc' | 'desc'
  onSort?: () => void
  filterContent?: (open: boolean) => ReactNode
  filterActive?: boolean
  onFilterOpenChange?: (open: boolean) => void
  children?: ReactNode
}) {
  const headerRef = useRef<HTMLTableCellElement>(null)
  const resizer = (
    <ResizeHandle
      label={`Resize ${label} column`}
      orientation="vertical"
      value={Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width ?? MIN_COLUMN_WIDTH))}
      min={MIN_COLUMN_WIDTH}
      max={MAX_COLUMN_WIDTH}
      measure={() => headerRef.current?.getBoundingClientRect().width}
      onChange={onWidthChange}
      onDragChange={onDragChange}
      onReset={onAutoSize}
      auto={auto}
      className={last ? 'right-0' : '-right-1'}
    />
  )
  const style = fixedColumnStyle(width)

  if (children === undefined && (sortable || filterContent)) {
    return (
      <SortableTh
        ref={headerRef}
        data-column-id={columnId}
        label={label}
        direction={direction}
        onSort={sortable ? onSort : undefined}
        filterContent={filterContent}
        filterActive={filterActive}
        onFilterOpenChange={onFilterOpenChange}
        style={style}
        resizer={resizer}
      />
    )
  }
  return (
    <Th ref={headerRef} data-column-id={columnId} aria-label={label} className="relative" style={style}>
      {children ?? label}{resizer}
    </Th>
  )
}

function fixedColumnStyle(width: number | undefined) {
  return width === undefined ? undefined : { width, minWidth: width, maxWidth: width }
}

function sizedTableStyle(widths: Record<string, number>, columns: readonly string[]) {
  const values = columns.flatMap((column) => widths[column] ?? [])
  return values.length !== columns.length || values.length === 0
    ? undefined
    : { width: values.reduce((sum, width) => sum + width, 0) }
}

function measureColumnWidths(table: HTMLTableElement | null) {
  if (!table) return {}
  return Object.fromEntries(
    [...table.querySelectorAll<HTMLTableCellElement>('thead th[data-column-id]')]
      .map((header) => [
        header.dataset.columnId!,
        header.getBoundingClientRect().width,
      ]),
  )
}

function resizeColumnAndTrailing(
  widths: Record<string, number>,
  columns: readonly string[],
  column: string,
  width: number,
) {
  const currentWidth = widths[column]
  if (currentWidth === undefined) return { ...widths, [column]: width }
  const trailing = columns.slice(columns.indexOf(column) + 1)
    .filter((id) => widths[id] !== undefined)
  if (trailing.length === 0) return { ...widths, [column]: width }

  const currentTrailingWidth = trailing.reduce((sum, id) => sum + widths[id]!, 0)
  const nextTrailingWidth = Math.max(
    MIN_COLUMN_WIDTH * trailing.length,
    currentTrailingWidth - (width - currentWidth),
  )
  return {
    ...widths,
    [column]: width,
    ...distributeColumnWidths(widths, trailing, nextTrailingWidth),
  }
}

function distributeColumnWidths(
  widths: Record<string, number>,
  columns: readonly string[],
  total: number,
) {
  const distributed: Record<string, number> = {}
  let remaining = [...columns]
  let remainingTotal = total

  while (remaining.length > 0) {
    const weight = remaining.reduce((sum, column) => sum + widths[column]!, 0)
    const belowMinimum = remaining.filter((column) =>
      (remainingTotal * widths[column]! / weight) < MIN_COLUMN_WIDTH)
    if (belowMinimum.length === 0) {
      for (const column of remaining) {
        distributed[column] = remainingTotal * widths[column]! / weight
      }
      break
    }
    for (const column of belowMinimum) distributed[column] = MIN_COLUMN_WIDTH
    remainingTotal -= MIN_COLUMN_WIDTH * belowMinimum.length
    remaining = remaining.filter((column) => !belowMinimum.includes(column))
  }

  return distributed
}

function formatValue(value: unknown, labels?: Readonly<Record<string, string>> | null) {
  if (value === null || value === undefined || value === '') return 'Not available'
  if (typeof value === 'string') return labels?.[value] ?? value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return 'Available'
}

export const DataGrid = Object.assign(DataGridRoot, {
  Root: DataGridRoot,
  Search: DataGridSearch,
  Filter: DataGridFilter,
  Filters: DataGridFilters,
  ColumnChooser: DataGridColumnChooser,
  Table: DataGridTable,
  LoadMore: DataGridLoadMore,
  SavedViews: DataGridSavedViews,
  Export: DataGridExport,
})
